/** Menu & recipe analysis tab - waste by menu item/ingredient, recipe yield variance, portion
 * control variance, ingredients shared across recipes, low-selling/high-waste items, and
 * suggested recipe/portion/menu changes. */

import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Section } from '../../../components/ui/Section';
import { applyRoundAndFormat } from '../../../utils';
import { RECIPE_WASTE } from '../data';
import { StatusChip } from './StatusChip';

interface MenuRecipeTabProps {
  currencyCode?: string;
}

const VarianceCell = ({ value }: { value: number }) => {
  const status = Math.abs(value) > 8 ? 'critical' : Math.abs(value) > 4 ? 'warning' : 'good';
  return <StatusChip status={status}
    label={`${value > 0 ? '+' : ''}${value.toFixed(1)}%`} />;
};

const MenuRecipeTab = ({ currencyCode }: MenuRecipeTabProps) => {
  const { t } = useTranslation('common');
  const lowSellingHighWaste = RECIPE_WASTE.filter((r) => r.salesRank === 'Low' && r.wasteCost > 1000);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textMD"
            fontWeight={600}
            sx={{ mb: 3 }}>
            {t('wasteDashboardRecipeAnalysisTitle', 'Waste by menu item, recipe and ingredient')}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('wasteDashboardMenuItem', 'Menu item')}</TableCell>
                <TableCell>{t('wasteDashboardRecipe', 'Recipe')}</TableCell>
                <TableCell>{t('wasteDashboardIngredient', 'Ingredient')}</TableCell>
                <TableCell align="right">{t('wasteDashboardWasteCost', 'Waste cost')}</TableCell>
                <TableCell>{t('wasteDashboardYieldVariance', 'Yield variance')}</TableCell>
                <TableCell>{t('wasteDashboardPortionVariance', 'Portion control variance')}</TableCell>
                <TableCell align="right">{t('wasteDashboardUsedInRecipes', 'Used in # recipes')}</TableCell>
                <TableCell>{t('wasteDashboardSalesRank', 'Sales rank')}</TableCell>
                <TableCell>{t('wasteDashboardSuggestedChange', 'Suggested change')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {RECIPE_WASTE.map((row) => (
                <TableRow key={row.recipe}>
                  <TableCell>{row.menuItem}</TableCell>
                  <TableCell>{row.recipe}</TableCell>
                  <TableCell>{row.ingredient}</TableCell>
                  <TableCell align="right">{applyRoundAndFormat(row.wasteCost, currencyCode)}</TableCell>
                  <TableCell><VarianceCell value={row.yieldVariancePercent} /></TableCell>
                  <TableCell><VarianceCell value={row.portionControlVariancePercent} /></TableCell>
                  <TableCell align="right">{row.usedInRecipeCount}</TableCell>
                  <TableCell>{row.salesRank}</TableCell>
                  <TableCell>{row.suggestedChange}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Section>

      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textMD"
            fontWeight={600}
            sx={{ mb: 3 }}>
            {t('wasteDashboardLowSellingHighWaste', 'Low-selling menu items with high waste')}
          </Typography>
          {lowSellingHighWaste.length === 0 ? (
            <Typography variant="textSM"
              color="text.secondary">{t('wasteDashboardNoLowSellingHighWaste', 'No low-selling, high-waste menu items for the current filters.')}</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('wasteDashboardMenuItem', 'Menu item')}</TableCell>
                  <TableCell align="right">{t('wasteDashboardWasteCost', 'Waste cost')}</TableCell>
                  <TableCell>{t('wasteDashboardSuggestedChange', 'Suggested change')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lowSellingHighWaste.map((row) => (
                  <TableRow key={row.recipe}>
                    <TableCell>{row.menuItem}</TableCell>
                    <TableCell align="right">{applyRoundAndFormat(row.wasteCost, currencyCode)}</TableCell>
                    <TableCell>{row.suggestedChange}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      </Section>
    </Box>
  );
};

export default MenuRecipeTab;
